import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface InventoryBalance {
	_id?: string
	id?: string
	productCode: string
	productName?: string
	productNameAr?: string
	outletCode: string
	outletName?: string
	balances: {
		openingQty: number
		currentQty: number
		averageCost: number
		totalValue: number
	}
	movements: {
		purchaseQty: number
		purchaseValue: number
		salesQty: number
		salesValue: number
		issueQty: number
		issueValue: number
		transferInQty: number
		transferInValue: number
		transferOutQty: number
		transferOutValue: number
		adjustmentQty: number
		adjustmentValue: number
		consumptionQty: number
		consumptionValue: number
	}
	lastTransactionDate?: string
	lastTransactionNo?: number
	lastTransactionType?: number
}

interface InventoryResponse {
	data: InventoryBalance[]
	pagination?: {
		page: number
		limit: number
		total: number
		totalPages: number
	}
}

interface InventoryParams {
	page?: number
	limit?: number
	outletCode?: string
	productCode?: string
	search?: string
	minQty?: number
	maxQty?: number
}

export const useInventoryBalances = (params: InventoryParams = {}) => {
	const { page = 1, limit = 20, outletCode, productCode, search, minQty, maxQty } = params

	return useQuery({
		queryKey: ['inventory', 'balances', { page, limit, outletCode, productCode, search, minQty, maxQty }],
		queryFn: () =>
			apiClient.get<InventoryResponse>('/inventory/balances/outlet', {
				outletCode,
				productCode,
				search,
				minQty,
				maxQty,
				page,
				limit,
			}),
		enabled: !!outletCode || !!productCode,
	})
}

export const useInventoryByOutlet = (outletCode: string) => {
	return useQuery({
		queryKey: ['inventory', 'outlet', outletCode],
		queryFn: () => apiClient.get<{ data: InventoryBalance[] }>(`/inventory/balances/outlet`, { outletCode }),
		enabled: !!outletCode,
	})
}

export const useInventoryByProduct = (productCode: string) => {
	return useQuery({
		queryKey: ['inventory', 'product', productCode],
		queryFn: () =>
			apiClient.get<{ data: InventoryBalance[] }>(`/inventory/balances/product`, { productCode }),
		enabled: !!productCode,
	})
}

export const useInventoryBalance = (productCode: string, outletCode: string) => {
	return useQuery({
		queryKey: ['inventory', 'balance', productCode, outletCode],
		queryFn: () =>
			apiClient.get<InventoryBalance>('/inventory/balance', {
				productCode,
				outletCode,
			}),
		enabled: !!productCode && !!outletCode,
	})
}
